/**
 * Vault Cryptography Utilities
 * Zero-knowledge encryption using Web Crypto API
 *
 * Algorithms:
 * - Key Derivation: PBKDF2-SHA256 with 600,000 iterations (OWASP 2023)
 * - Encryption: AES-256-GCM (authenticated encryption)
 *
 * @module vaultCrypto
 */

// Constants (match backend crypto.service.ts)
const PBKDF2_ITERATIONS = 600000;
const IV_LENGTH = 12; // bytes (96 bits for GCM)
const AUTH_TAG_LENGTH = 16; // bytes (128 bits)
const KEY_LENGTH = 32; // bytes (256 bits)

/**
 * Generate cryptographically secure random bytes
 */
function getRandomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * Derive master encryption key from password using PBKDF2
 */
export async function deriveMasterKey(
  password: string,
  salt: Uint8Array
): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  // Import password as CryptoKey
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  // Derive key using PBKDF2
  const masterKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as unknown as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    passwordKey,
    { name: 'AES-GCM', length: KEY_LENGTH * 8 },
    true, // extractable (needed for storage/export)
    ['encrypt', 'decrypt']
  );

  return masterKey;
}

/**
 * Generate a random document encryption key
 */
export async function generateDocumentKey(): Promise<CryptoKey> {
  return await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: KEY_LENGTH * 8 },
    true, // extractable
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt document key with master key
 */
export async function encryptDocumentKey(
  documentKey: CryptoKey,
  masterKey: CryptoKey
): Promise<{
  encryptedKey: string; // base64
  iv: string; // base64
  authTag: string; // base64
}> {
  // Export document key as raw bytes
  const rawDocumentKey = (await crypto.subtle.exportKey(
    'raw',
    documentKey
  )) as ArrayBuffer;

  // Generate random IV
  const iv = getRandomBytes(IV_LENGTH);

  // Encrypt with master key
  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv as unknown as BufferSource,
      tagLength: AUTH_TAG_LENGTH * 8,
    },
    masterKey,
    rawDocumentKey
  );

  // GCM produces: encryptedData + authTag
  // Split them
  const encryptedArray = new Uint8Array(encrypted);
  const ciphertext = encryptedArray.slice(0, -AUTH_TAG_LENGTH);
  const authTag = encryptedArray.slice(-AUTH_TAG_LENGTH);

  return {
    encryptedKey: arrayBufferToBase64(ciphertext.buffer),
    iv: arrayBufferToBase64(iv),
    authTag: arrayBufferToBase64(authTag),
  };
}

/**
 * Decrypt document key with master key
 */
export async function decryptDocumentKey(
  encryptedKey: string, // base64
  iv: string, // base64
  authTag: string, // base64
  masterKey: CryptoKey
): Promise<CryptoKey> {
  const encryptedBuffer = base64ToArrayBuffer(encryptedKey);
  const ivBuffer = base64ToArrayBuffer(iv);
  const authTagBuffer = base64ToArrayBuffer(authTag);

  // Reconstruct GCM ciphertext: encryptedData + authTag
  const combined = new Uint8Array(
    encryptedBuffer.byteLength + authTagBuffer.byteLength
  );
  combined.set(new Uint8Array(encryptedBuffer), 0);
  combined.set(new Uint8Array(authTagBuffer), encryptedBuffer.byteLength);

  // Decrypt
  const rawDocumentKey = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(ivBuffer),
      tagLength: AUTH_TAG_LENGTH * 8,
    },
    masterKey,
    combined.buffer
  );

  // Import as CryptoKey
  return await crypto.subtle.importKey(
    'raw',
    rawDocumentKey,
    { name: 'AES-GCM', length: KEY_LENGTH * 8 },
    true,
    ['encrypt', 'decrypt']
  );
}

/**
 * Encrypt document content
 */
export async function encryptDocument(
  fileBuffer: ArrayBuffer,
  documentKey: CryptoKey
): Promise<{
  encryptedContent: ArrayBuffer;
  iv: string; // base64
  authTag: string; // base64
}> {
  const iv = getRandomBytes(IV_LENGTH);

  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: iv as unknown as BufferSource,
      tagLength: AUTH_TAG_LENGTH * 8,
    },
    documentKey,
    fileBuffer
  );

  // Split ciphertext and auth tag
  const encryptedArray = new Uint8Array(encrypted);
  const ciphertext = encryptedArray.slice(0, -AUTH_TAG_LENGTH);
  const authTag = encryptedArray.slice(-AUTH_TAG_LENGTH);

  return {
    encryptedContent: ciphertext.buffer as ArrayBuffer,
    iv: arrayBufferToBase64(iv),
    authTag: arrayBufferToBase64(authTag),
  };
}

/**
 * Decrypt document content
 */
export async function decryptDocument(
  encryptedContent: ArrayBuffer,
  iv: string, // base64
  authTag: string, // base64
  documentKey: CryptoKey
): Promise<ArrayBuffer> {
  const ivBuffer = base64ToArrayBuffer(iv);
  const authTagBuffer = base64ToArrayBuffer(authTag);

  // Reconstruct GCM ciphertext
  const combined = new Uint8Array(
    encryptedContent.byteLength + authTagBuffer.byteLength
  );
  combined.set(new Uint8Array(encryptedContent), 0);
  combined.set(new Uint8Array(authTagBuffer), encryptedContent.byteLength);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(ivBuffer),
      tagLength: AUTH_TAG_LENGTH * 8,
    },
    documentKey,
    combined.buffer
  );

  return decrypted;
}

/**
 * Calculate SHA-256 hash of content (for integrity verification)
 */
export async function calculateHash(content: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', content);
  return arrayBufferToBase64(hashBuffer);
}

/**
 * Initialize vault: derive master key and prepare encryption metadata
 */
import {
  generateSalt,
  deriveKeyFromPassword,
  generateVaultMasterKey,
  encryptVaultKey,
  decryptVaultKey,
  generateRSAKeyPair,
  exportPublicKeyToPEM,
  encryptRSAPrivateKey,
  arrayBufferToBase64,
  base64ToArrayBuffer,
  EncryptedVaultKey,
} from '../crypto';
import { EncryptedVaultKeyData } from '@/types/vault.types';

/**
 * Initialize vault:
 * 1. Derive KEK from password
 * 2. Generate random Vault Master Key (DEK)
 * 3. Encrypt DEK with KEK
 * 4. Generate RSA Key Pair
 * 5. Encrypt RSA Private Key with DEK
 * 6. Return DEK (for session) and Init Data (for server)
 */
export async function initializeVault(password: string): Promise<{
  masterKey: CryptoKey;
  initData: {
    vaultKeySalt: string;
    encryptedVaultKey: EncryptedVaultKeyData;
    encryptionParams: {
      algorithm: string;
      iterations: number;
      keyDerivation: string;
    };
    publicKey: string;
    encryptedPrivateKey: string;
  };
}> {
  // 1. Generate Salt & Derive KEK
  const salt = generateSalt();
  const kek = await deriveKeyFromPassword(password, salt, PBKDF2_ITERATIONS);

  // 2. Generate Vault Master Key (DEK)
  const masterKey = await generateVaultMasterKey();

  // 3. Encrypt DEK with KEK
  const encryptedVaultKey = await encryptVaultKey(masterKey, kek);

  // 4. Generate RSA Key Pair
  const rsaKeyPair = await generateRSAKeyPair();

  // 5. Encrypt RSA Private Key with DEK
  const encryptedPrivateKeyObj = await encryptRSAPrivateKey(
    rsaKeyPair.privateKey,
    masterKey
  );

  // 6. Export Public Key
  const publicKeyPEM = await exportPublicKeyToPEM(rsaKeyPair.publicKey);

  // Format Data for Server
  const encryptedVaultKeyData: EncryptedVaultKeyData = {
    encryptedKey: arrayBufferToBase64(encryptedVaultKey.encryptedKey),
    iv: arrayBufferToBase64(encryptedVaultKey.iv),
    authTag: arrayBufferToBase64(encryptedVaultKey.authTag),
  };

  const encryptedPrivateKeyStr = JSON.stringify({
    encryptedKey: arrayBufferToBase64(encryptedPrivateKeyObj.encryptedKey),
    iv: arrayBufferToBase64(encryptedPrivateKeyObj.iv),
    authTag: arrayBufferToBase64(encryptedPrivateKeyObj.authTag),
  });

  return {
    masterKey,
    initData: {
      vaultKeySalt: arrayBufferToBase64(salt.buffer as ArrayBuffer),
      encryptedVaultKey: encryptedVaultKeyData,
      encryptionParams: {
        algorithm: 'AES-256-GCM',
        iterations: PBKDF2_ITERATIONS,
        keyDerivation: 'PBKDF2-SHA256',
      },
      publicKey: publicKeyPEM,
      encryptedPrivateKey: encryptedPrivateKeyStr,
    },
  };
}

/**
 * Unlock vault:
 * 1. Derive KEK from password + salt
 * 2. Decrypt encryptedVaultKey with KEK to get DEK (actual master key)
 * 3. Return DEK
 *
 * @param password - User password
 * @param saltBase64 - Base64-encoded salt
 * @param encryptedVaultKeyData - Encrypted vault key data from server
 * @returns The vault master key (DEK) for encrypting/decrypting documents
 */
export async function unlockVault(
  password: string,
  saltBase64: string,
  encryptedVaultKeyData: EncryptedVaultKeyData
): Promise<CryptoKey> {
  // 1. Derive KEK from password + salt
  const salt = new Uint8Array(base64ToArrayBuffer(saltBase64));
  const kek = await deriveMasterKey(password, salt);

  // 2. Prepare encrypted vault key structure for decryption
  const encryptedVaultKey: EncryptedVaultKey = {
    encryptedKey: base64ToArrayBuffer(encryptedVaultKeyData.encryptedKey),
    iv: new Uint8Array(base64ToArrayBuffer(encryptedVaultKeyData.iv)),
    authTag: new Uint8Array(base64ToArrayBuffer(encryptedVaultKeyData.authTag)),
  };

  // 3. Decrypt encryptedVaultKey to get DEK (actual vault master key)
  const dek = await decryptVaultKey(encryptedVaultKey, kek);

  return dek;
}

// Export types
export interface EncryptedDocument {
  encryptedContent: ArrayBuffer;
  encryptedDocumentKey: string;
  iv: string;
  authTag: string;
  documentKeyIv: string;
  documentKeyAuthTag: string;
  contentHash: string;
}

/**
 * Full encryption workflow: encrypt document and its key
 */
export async function encryptDocumentComplete(
  fileBuffer: ArrayBuffer,
  masterKey: CryptoKey
): Promise<EncryptedDocument> {
  // 1. Generate random document key
  const documentKey = await generateDocumentKey();

  // 2. Encrypt document with document key
  const {
    encryptedContent,
    iv: documentIv,
    authTag: documentAuthTag,
  } = await encryptDocument(fileBuffer, documentKey);

  // 3. Encrypt document key with master key
  const {
    encryptedKey: encryptedDocumentKey,
    iv: keyIv,
    authTag: keyAuthTag,
  } = await encryptDocumentKey(documentKey, masterKey);

  // 4. Calculate original content hash
  const contentHash = await calculateHash(fileBuffer);

  return {
    encryptedContent,
    encryptedDocumentKey,
    iv: documentIv,
    authTag: documentAuthTag,
    documentKeyIv: keyIv,
    documentKeyAuthTag: keyAuthTag,
    contentHash,
  };
}

/**
 * Full decryption workflow: decrypt document key then document
 * For documents encrypted client-side (AES-GCM encrypted DEK)
 */
export async function decryptDocumentComplete(
  encryptedContent: ArrayBuffer,
  encryptedDocumentKey: string,
  documentIv: string,
  documentAuthTag: string,
  documentKeyIv: string,
  documentKeyAuthTag: string,
  masterKey: CryptoKey
): Promise<ArrayBuffer> {
  // 1. Decrypt document key with master key
  const documentKey = await decryptDocumentKey(
    encryptedDocumentKey,
    documentKeyIv,
    documentKeyAuthTag,
    masterKey
  );

  // 2. Decrypt document with document key
  const decrypted = await decryptDocument(
    encryptedContent,
    documentIv,
    documentAuthTag,
    documentKey
  );

  return decrypted;
}

/**
 * Full decryption workflow for RSA-encrypted documents
 * For documents encrypted server-side during sync (RSA-encrypted DEK)
 */
export async function decryptDocumentWithRSA(
  encryptedContent: ArrayBuffer,
  encryptedDocumentKey: string, // Base64 RSA-encrypted DEK
  documentIv: string,
  documentAuthTag: string,
  encryptedPrivateKey: string, // JSON string with encrypted RSA private key
  masterKey: CryptoKey
): Promise<ArrayBuffer> {
  // 1. Parse encrypted private key data
  const privateKeyData = JSON.parse(encryptedPrivateKey);
  const encryptedPrivateKeyObj = {
    encryptedKey: base64ToArrayBuffer(privateKeyData.encryptedKey),
    iv: new Uint8Array(base64ToArrayBuffer(privateKeyData.iv)),
    authTag: new Uint8Array(base64ToArrayBuffer(privateKeyData.authTag)),
  };

  // 2. Decrypt RSA private key using master key
  const { decryptRSAPrivateKey, decryptFileKeyWithRSA } = await import('../crypto');
  const rsaPrivateKey = await decryptRSAPrivateKey(encryptedPrivateKeyObj, masterKey);

  // 3. Decrypt document key (DEK) using RSA private key
  const encryptedDekBuffer = base64ToArrayBuffer(encryptedDocumentKey);
  const decryptedDekRaw = await decryptFileKeyWithRSA(encryptedDekBuffer, rsaPrivateKey);

  // 4. Import DEK as CryptoKey
  const documentKey = await crypto.subtle.importKey(
    'raw',
    decryptedDekRaw,
    { name: 'AES-GCM', length: KEY_LENGTH * 8 },
    false,
    ['decrypt']
  );

  // 5. Decrypt document with DEK
  const decrypted = await decryptDocument(
    encryptedContent,
    documentIv,
    documentAuthTag,
    documentKey
  );

  return decrypted;
}

/**
 * Migrate vault keys: Generate RSA keys for an existing vault
 */
export async function migrateVaultKeys(masterKey: CryptoKey): Promise<{
  publicKey: string;
  encryptedPrivateKey: string;
}> {
  // 1. Generate RSA Key Pair
  const rsaKeyPair = await generateRSAKeyPair();

  // 2. Encrypt RSA Private Key with Master Key
  const encryptedPrivateKeyObj = await encryptRSAPrivateKey(
    rsaKeyPair.privateKey,
    masterKey
  );

  // 3. Export Public Key
  const publicKeyPEM = await exportPublicKeyToPEM(rsaKeyPair.publicKey);

  // 4. Format Encrypted Private Key
  const encryptedPrivateKeyStr = JSON.stringify({
    encryptedKey: arrayBufferToBase64(encryptedPrivateKeyObj.encryptedKey),
    iv: arrayBufferToBase64(encryptedPrivateKeyObj.iv),
    authTag: arrayBufferToBase64(encryptedPrivateKeyObj.authTag),
  });

  return {
    publicKey: publicKeyPEM,
    encryptedPrivateKey: encryptedPrivateKeyStr,
  };
}
